/**
 * ============================================================================
 * VUETIFY.JS - CONFIGURACION DEL FRAMEWORK DE UI
 * ============================================================================
 *
 * PROPOSITO:
 * Este archivo configura Vuetify, el framework de UI que usamos para crear
 * la interfaz de usuario. Vuetify implementa Material Design de Google,
 * proporcionando componentes listos para usar como botones, tarjetas, tablas, etc.
 *
 * QUE ES VUETIFY:
 * Vuetify es una libreria de componentes de UI para Vue.js que sigue las
 * guias de Material Design. Te da componentes pre-construidos y estilizados
 * para que no tengas que escribir CSS desde cero.
 *
 * SIN VUETIFY tendrias que:
 * - Crear tu propio componente de boton con estilos
 * - Crear tu propio componente de tarjeta
 * - Crear tu propio componente de tabla con paginacion
 * - Manejar responsive design manualmente
 * - etc.
 *
 * CON VUETIFY solo escribes:
 *   <v-btn color="primary">Guardar</v-btn>
 *   <v-card>Contenido</v-card>
 *   <v-data-table :items="datos" />
 *
 * RELACION CON OTROS ARCHIVOS:
 * - main.js: Importa este archivo y lo registra con app.use(vuetify)
 * - App.vue: Debe tener <v-app> como contenedor raiz
 * - Todos los componentes: Usan componentes de Vuetify (v-btn, v-card, etc.)
 *
 * MATERIAL DESIGN:
 * Es un sistema de diseno creado por Google que define:
 * - Como deben verse los componentes (botones, inputs, cards)
 * - Como deben comportarse (animaciones, transiciones)
 * - Paleta de colores y tipografia
 * - Espaciado y elevaciones (sombras)
 * ============================================================================
 */

// ============================================================================
// IMPORTACIONES DE VUETIFY
// ============================================================================

/**
 * Estilos base de Vuetify.
 *
 * Esta importacion carga todos los estilos CSS que Vuetify necesita
 * para que sus componentes se vean correctamente.
 *
 * INCLUYE:
 * - Variables CSS (colores, espaciados, tipografia)
 * - Estilos base de todos los componentes
 * - Sistema de grid (v-row, v-col)
 * - Clases utilitarias (pa-4, ma-2, d-flex, etc.)
 *
 * IMPORTANTE: Esta linea DEBE estar antes de createVuetify()
 * Si la quitas, los componentes de Vuetify se veran rotos.
 */
import 'vuetify/styles'

/**
 * createVuetify: Funcion para crear la instancia de Vuetify.
 *
 * Similar a createApp de Vue, esta funcion crea y configura Vuetify.
 * Retorna un objeto que luego registramos con app.use(vuetify).
 */
import { createVuetify } from 'vuetify'

/**
 * Importar TODOS los componentes de Vuetify.
 *
 * * as components significa "importa todo y ponlo en un objeto llamado components"
 *
 * Esto nos da acceso a componentes como:
 * - components.VBtn
 * - components.VCard
 * - components.VTextField
 * - components.VDataTable
 * - etc. (mas de 80 componentes)
 *
 * ALTERNATIVA MAS EFICIENTE (Tree Shaking):
 * Si quieres reducir el tamaño del bundle, puedes importar
 * solo los componentes que usas:
 *
 *   import { VBtn, VCard, VTextField } from 'vuetify/components'
 *
 * Pero para desarrollo es mas comodo tenerlos todos disponibles.
 */
import * as components from 'vuetify/components'

/**
 * Importar TODAS las directivas de Vuetify.
 *
 * Las directivas son atributos especiales que modifican el comportamiento
 * de los elementos. Vuetify incluye directivas como:
 *
 * - v-ripple: Efecto de onda al hacer click (Material Design)
 * - v-resize: Detectar cambios de tamaño de ventana
 * - v-scroll: Detectar eventos de scroll
 * - v-touch: Manejar gestos tactiles
 *
 * Ejemplo de uso:
 *   <div v-ripple>Click me para ver el efecto ripple</div>
 */
import * as directives from 'vuetify/directives'

/**
 * Material Design Icons (MDI).
 *
 * Esta importacion carga la fuente de iconos de Material Design.
 * Vuetify usa estos iconos por defecto en muchos de sus componentes.
 *
 * COMO SE USAN:
 *   <v-icon>mdi-home</v-icon>
 *   <v-icon>mdi-plus</v-icon>
 *   <v-icon>mdi-account</v-icon>
 *   <v-btn prepend-icon="mdi-save">Guardar</v-btn>
 *
 * CATALOGO DE ICONOS:
 * Puedes ver todos los iconos disponibles en:
 * https://materialdesignicons.com/
 *
 * Hay mas de 7000 iconos disponibles, incluyendo:
 * - mdi-file-document: Documento
 * - mdi-receipt: Factura
 * - mdi-account: Usuario
 * - mdi-cog: Configuracion
 * - mdi-check: Checkmark
 * - mdi-close: X (cerrar)
 * - mdi-plus: Signo mas
 * - mdi-minus: Signo menos
 * - mdi-trash-can: Eliminar
 */
import '@mdi/font/css/materialdesignicons.css'


// ============================================================================
// CONFIGURACION DE TEMAS (CLARO Y OSCURO)
// ============================================================================

/**
 * SISTEMA DE TEMAS
 *
 * El sistema soporta:
 * - Tema claro (lightTheme) - Por defecto
 * - Tema oscuro (darkTheme) - Reduce fatiga visual
 * - Variantes de color - Diferentes paletas de colores primarios
 * - Persistencia en localStorage - Se guarda la preferencia del usuario
 */

// Clave para guardar preferencias en localStorage
const THEME_STORAGE_KEY = 'dte-theme-preference'

/**
 * Variantes de color disponibles - Paleta Moderna 2024
 * Cada variante define colores primary, secondary y accent con tonos sofisticados
 * IMPORTANTE: Los nombres deben coincidir con los usados en AppHeader.vue y theme.js
 */
export const colorVariants = {
  // Azul moderno - Default (inspirado en Tailwind/Shadcn)
  default: { primary: '#2563EB', secondary: '#7C3AED', accent: '#0EA5E9' },
  // Índigo profundo
  indigo: { primary: '#4F46E5', secondary: '#6366F1', accent: '#818CF8' },
  // Verde azulado (Teal)
  teal: { primary: '#00897B', secondary: '#26A69A', accent: '#4DB6AC' },
  // Verde
  green: { primary: '#2E7D32', secondary: '#43A047', accent: '#66BB6A' },
  // Naranja
  orange: { primary: '#EF6C00', secondary: '#F57C00', accent: '#FFA726' },
  // Morado
  purple: { primary: '#7B1FA2', secondary: '#8E24AA', accent: '#AB47BC' }
}

/**
 * Obtiene la preferencia guardada del localStorage
 */
const getSavedPreference = () => {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY)
    if (saved) {
      return JSON.parse(saved)
    }
  } catch (e) {
    // Ignorar errores
  }
  return { theme: 'lightTheme', variant: 'default' }
}

// Cargar preferencias guardadas
const savedPref = getSavedPreference()
const currentVariant = colorVariants[savedPref.variant] || colorVariants.default

/**
 * Colores de estado modernos - Mejor contraste y legibilidad
 */
const stateColors = {
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#0EA5E9',
}

/**
 * TEMA CLARO (Light Theme) - Diseño Moderno
 * Fondo claro con texto oscuro - ideal para uso diurno
 * Inspirado en interfaces modernas como Linear, Notion, Vercel
 */
const lightTheme = {
  dark: false,
  colors: {
    // Colores de la variante
    primary: currentVariant.primary,
    secondary: currentVariant.secondary,
    accent: currentVariant.accent,
    // Colores de estado
    ...stateColors,
    // Colores de superficie - Escala de grises refinada
    background: '#F8FAFC',
    surface: '#FFFFFF',
    'surface-variant': '#F1F5F9',
    'surface-bright': '#FFFFFF',
    // Colores de texto - Mayor contraste y legibilidad
    'on-primary': '#FFFFFF',
    'on-secondary': '#FFFFFF',
    'on-surface': '#0F172A',
    'on-background': '#0F172A',
    'on-surface-variant': '#475569',
    // Colores personalizados
    'grey-light': '#F8FAFC',
    'grey-border': '#E2E8F0',
    'grey-muted': '#94A3B8',
  }
}

/**
 * TEMA OSCURO (Dark Theme) - Diseño Moderno
 * Fondo oscuro con texto claro - reduce fatiga visual en ambientes oscuros
 * Inspirado en interfaces modernas como GitHub Dark, Discord, Linear
 *
 * PALETA DE GRISES PARA MODO OSCURO:
 * - background: Fondo principal más oscuro
 * - surface: Cards, dialogs, inputs - gris más claro para contraste
 * - surface-variant: Headers, áreas secundarias
 */
const darkTheme = {
  dark: true,
  colors: {
    // Colores de la variante
    primary: currentVariant.primary,
    secondary: currentVariant.secondary,
    accent: currentVariant.accent,
    // Colores de estado
    ...stateColors,
    // Colores de superficie para tema oscuro - Escala refinada
    background: '#0F172A',       // Fondo principal - slate muy oscuro (más sofisticado que negro puro)
    surface: '#1E293B',          // Cards y superficies - con toque azulado
    'surface-variant': '#334155', // Headers, áreas secundarias
    'surface-bright': '#475569', // Para inputs y elementos que necesitan destacar
    // Colores de texto para tema oscuro - CLAROS para contraste
    'on-primary': '#FFFFFF',
    'on-secondary': '#FFFFFF',
    'on-surface': '#F1F5F9',     // Texto principal - casi blanco
    'on-surface-variant': '#CBD5E1', // Texto secundario
    'on-background': '#F1F5F9',
    // Colores personalizados para oscuro
    'grey-light': '#1E293B',
    'grey-border': '#334155',
    'grey-muted': '#64748B',
  }
}

/**
 * Funcion para actualizar los colores de una variante
 * Se llama desde el componente de seleccion de tema
 *
 * @param {Object} themeInstance - Instancia de useTheme() de Vuetify
 * @param {string} variantName - Nombre de la variante ('default', 'indigo', etc.)
 */
export const updateThemeVariant = (themeInstance, variantName) => {
  const variant = colorVariants[variantName] || colorVariants.default

  // useTheme() retorna { themes, global, ... }
  // themes.value contiene los temas definidos
  const themes = themeInstance.themes?.value || themeInstance.themes

  if (!themes) {
    console.warn('No se pudo acceder a los temas de Vuetify')
    return
  }

  // Actualizar tema claro
  if (themes.lightTheme) {
    themes.lightTheme.colors.primary = variant.primary
    themes.lightTheme.colors.secondary = variant.secondary
    themes.lightTheme.colors.accent = variant.accent
  }

  // Actualizar tema oscuro
  if (themes.darkTheme) {
    themes.darkTheme.colors.primary = variant.primary
    themes.darkTheme.colors.secondary = variant.secondary
    themes.darkTheme.colors.accent = variant.accent
  }
}


// ============================================================================
// CREACION DE LA INSTANCIA DE VUETIFY
// ============================================================================

/**
 * createVuetify(): Crea y configura la instancia de Vuetify.
 *
 * Esta funcion acepta un objeto de configuracion con:
 * - components: Componentes de Vuetify a registrar
 * - directives: Directivas de Vuetify a registrar
 * - theme: Configuracion de temas
 * - defaults: Valores por defecto para componentes
 * - icons: Configuracion de iconos
 * - display: Configuracion de breakpoints
 * - etc.
 *
 * La instancia creada se exporta y se usa en main.js con app.use(vuetify)
 */
const vuetify = createVuetify({
  /**
   * components: Registrar todos los componentes de Vuetify.
   *
   * Al pasar el objeto 'components' (que importamos arriba),
   * Vuetify registra TODOS sus componentes globalmente.
   *
   * Esto significa que puedes usar cualquier componente de Vuetify
   * en cualquier parte de la aplicacion sin importarlo:
   *
   *   <v-btn>No necesitas importar VBtn</v-btn>
   *   <v-card>No necesitas importar VCard</v-card>
   *
   * ALTERNATIVA (para bundles mas pequeños):
   * Puedes importar y registrar solo los componentes que usas:
   *
   *   import { VBtn, VCard } from 'vuetify/components'
   *   components: { VBtn, VCard }
   */
  components,

  /**
   * directives: Registrar todas las directivas de Vuetify.
   *
   * Las directivas son atributos especiales para los elementos.
   * Al registrarlas, puedes usarlas en cualquier template:
   *
   *   <div v-ripple>Efecto ripple al click</div>
   *   <div v-resize="onResize">Detecta cambios de tamaño</div>
   */
  directives,

  /**
   * theme: Configuracion de temas de la aplicacion.
   *
   * Vuetify soporta multiples temas y cambio dinamico.
   * Aqui definimos:
   * - Cual es el tema por defecto
   * - Que temas estan disponibles
   */
  theme: {
    /**
     * defaultTheme: Tema a usar por defecto.
     * Se carga desde localStorage si existe una preferencia guardada.
     */
    defaultTheme: savedPref.theme || 'lightTheme',

    /**
     * themes: Temas disponibles.
     * - lightTheme: Tema claro (fondo blanco/gris)
     * - darkTheme: Tema oscuro (fondo negro/gris oscuro)
     */
    themes: {
      lightTheme,
      darkTheme
    }
  },

  /**
   * defaults: Valores por defecto para componentes.
   *
   * Aqui puedes configurar props por defecto para cualquier
   * componente de Vuetify. Esto evita repetir las mismas props
   * en cada uso del componente.
   *
   * EJEMPLO SIN DEFAULTS:
   *   <v-btn variant="flat" rounded="lg">Btn 1</v-btn>
   *   <v-btn variant="flat" rounded="lg">Btn 2</v-btn>
   *   <v-btn variant="flat" rounded="lg">Btn 3</v-btn>
   *
   * CON DEFAULTS:
   *   <v-btn>Btn 1</v-btn>  <- Automaticamente tiene variant="flat" rounded="lg"
   *   <v-btn>Btn 2</v-btn>
   *   <v-btn>Btn 3</v-btn>
   *
   * Puedes sobreescribir los defaults en casos especificos:
   *   <v-btn variant="outlined">Diferente</v-btn>
   */
  /**
   * defaults: Valores por defecto modernos para componentes.
   * Diseño minimalista y limpio inspirado en Shadcn/Radix UI
   */
  defaults: {
    // ==========================================
    // GLOBAL - Aplicar a todos los componentes
    // ==========================================
    global: {
      ripple: true,
    },

    // ==========================================
    // BOTONES - Estilo moderno y elegante
    // ==========================================
    VBtn: {
      variant: 'flat',
      rounded: 'lg',
      class: 'text-none font-weight-medium',
    },

    // ==========================================
    // CARDS - Sombras sutiles, bordes limpios
    // ==========================================
    VCard: {
      rounded: 'xl',
      elevation: 0,
      border: true,
      class: 'border-opacity-50',
    },

    VCardTitle: {
      class: 'text-h6 font-weight-semibold',
    },

    // ==========================================
    // INPUTS - Estilo limpio y consistente
    // ==========================================
    VTextField: {
      variant: 'outlined',
      density: 'comfortable',
      rounded: 'lg',
      hideDetails: 'auto',
      color: 'primary',
    },

    VSelect: {
      variant: 'outlined',
      density: 'comfortable',
      rounded: 'lg',
      hideDetails: 'auto',
      color: 'primary',
    },

    VAutocomplete: {
      variant: 'outlined',
      density: 'comfortable',
      rounded: 'lg',
      hideDetails: 'auto',
      color: 'primary',
    },

    VTextarea: {
      variant: 'outlined',
      density: 'comfortable',
      rounded: 'lg',
      hideDetails: 'auto',
      color: 'primary',
    },

    VCombobox: {
      variant: 'outlined',
      density: 'comfortable',
      rounded: 'lg',
      hideDetails: 'auto',
      color: 'primary',
    },

    VFileInput: {
      variant: 'outlined',
      density: 'comfortable',
      rounded: 'lg',
      hideDetails: 'auto',
      color: 'primary',
    },

    // ==========================================
    // CHIPS - Modernos y compactos
    // ==========================================
    VChip: {
      rounded: 'lg',
      variant: 'tonal',
    },

    // ==========================================
    // ALERTAS - Estilo limpio con borde lateral
    // ==========================================
    VAlert: {
      rounded: 'lg',
      variant: 'tonal',
      border: 'start',
      borderColor: undefined,
    },

    // ==========================================
    // DIALOGOS - Sombras elegantes
    // ==========================================
    VDialog: {
      rounded: 'xl',
      scrim: 'black',
    },

    // ==========================================
    // TABS - Estilo limpio
    // ==========================================
    VTabs: {
      color: 'primary',
      sliderColor: 'primary',
    },

    VTab: {
      rounded: 'lg',
    },

    // ==========================================
    // TABLAS - Diseño limpio y moderno
    // ==========================================
    VDataTable: {
      hover: true,
      fixedHeader: true,
    },

    VTable: {
      hover: true,
    },

    // ==========================================
    // SWITCHES Y CHECKBOXES
    // ==========================================
    VSwitch: {
      color: 'primary',
      inset: true,
      hideDetails: 'auto',
    },

    VCheckbox: {
      color: 'primary',
      hideDetails: 'auto',
    },

    VRadio: {
      color: 'primary',
    },

    VRadioGroup: {
      color: 'primary',
      hideDetails: 'auto',
    },

    // ==========================================
    // LISTAS - Navegación suave
    // ==========================================
    VList: {
      rounded: 'lg',
    },

    VListItem: {
      rounded: 'lg',
    },

    // ==========================================
    // MENUS Y TOOLTIPS
    // ==========================================
    VMenu: {
      transition: 'scale-transition',
    },

    VTooltip: {
      location: 'top',
    },

    // ==========================================
    // NAVEGACIÓN
    // ==========================================
    VNavigationDrawer: {
      elevation: 0,
    },

    VAppBar: {
      elevation: 0,
    },

    // ==========================================
    // PROGRESS - Indicadores de carga
    // ==========================================
    VProgressLinear: {
      rounded: true,
      color: 'primary',
    },

    VProgressCircular: {
      color: 'primary',
    },

    // ==========================================
    // SNACKBAR - Notificaciones
    // ==========================================
    VSnackbar: {
      rounded: 'lg',
      timeout: 4000,
    },

    // ==========================================
    // EXPANSION PANELS
    // ==========================================
    VExpansionPanels: {
      rounded: 'lg',
      variant: 'accordion',
    },

    VExpansionPanel: {
      rounded: 'lg',
    },

    // ==========================================
    // SKELETON LOADERS
    // ==========================================
    VSkeletonLoader: {
      boilerplate: false,
    },

    // ==========================================
    // AVATARS
    // ==========================================
    VAvatar: {
      rounded: 'lg',
    },

    // ==========================================
    // BADGES
    // ==========================================
    VBadge: {
      rounded: 'lg',
    },

    // ==========================================
    // DIVIDERS
    // ==========================================
    VDivider: {
      class: 'border-opacity-50',
    },
  }
})


// ============================================================================
// EXPORTACION
// ============================================================================

/**
 * Exportar la instancia de Vuetify.
 *
 * Esta instancia se importa en main.js y se registra:
 *   import vuetify from './plugins/vuetify'
 *   app.use(vuetify)
 *
 * Despues de esto, Vuetify esta completamente configurado y
 * todos sus componentes estan disponibles globalmente.
 */
export default vuetify
